import { observer } from "mobx-react";
import { getEstimaticsCurrentProjectStore, getUserStore } from "../../../../data/DataStores";
import { getProcessedEstimatesStore, getProcessedEstimatesStoreDebug } from "../../../../data/ProcessedEstimatesStore";
import { logDebug } from "../../../../shared/common/logger";
import { IQueuedItemToProcess, QueuedItemToProcessStatus } from "../../../../shared/common/EstimateEngine/EstimateEngineTypes";
import SharedUtils from "../../../../shared/common/SharedUtils";
import { appFeatures } from "../../../../shared/common/AppFeatures";
import RestAPI from "../../../../utils/restAPI";
import { OneMeasurementCell } from "../../NewProjectWizard/NewProjectWizardStep3";
import { measurementCellNames } from "../../../../shared/common/EstimateEngine/EngineSharedUtils";
import { useEffect, useState } from "react";
import TopMostPortal from "../../../../components/ui/utils/Portals";
import EngineUtils from "../../../../shared/common/EngineUtils";
import { toastNotify } from "../../../../shared/client/FrontendUtils";
import MergeButton from "../../../../components/ui/utils/MergeButton";

export const MeasurementPanel: React.FC<{
	pageId: string;
	roomName: string;
	isWizard: boolean;
	itemForDebugOnly?: IQueuedItemToProcess | null;
}> = observer(({ roomName, isWizard, itemForDebugOnly, pageId }) => {
	const [showMergeWindow, setShowMergeWindow] = useState(false);
	const [mergePageId, setMergePageId] = useState("");
	const currentProjectStore = getEstimaticsCurrentProjectStore();
	const processedEstimatesStore = getProcessedEstimatesStore();
	const processedEstimatesStoreDebug = getProcessedEstimatesStoreDebug();
	useEffect(() => {
		// find room with the same name as this one and set it as mergePageId
		const pageData = processedEstimatesStore.allCurrentViewPages.find((pageInfo) => {
			const data = EngineUtils.pageDataFromPageInfo(pageInfo);
			// logDebug("data", data, pageId, roomName);
			if (data.roomName.toLowerCase() === roomName.toLowerCase() && data.pageId !== pageId) {
				return true;
			}
		});
		// logDebug("pageData", pageData);
		if (pageData) {
			const data = EngineUtils.pageDataFromPageInfo(pageData);
			setMergePageId(data.pageId);
		} else {
			const allNotThisOne = processedEstimatesStore.allCurrentViewPages
				.map((pageInfo, index) => {
					const pageData = EngineUtils.pageDataFromPageInfo(pageInfo);
					return pageData.pageId === pageId ? null : pageData.pageId;
				})
				.filter((pageId) => pageId);
			if (allNotThisOne.length > 0) {
				setMergePageId(allNotThisOne[0]!);
			}
		}
	}, [showMergeWindow]);
	const mergedWith = processedEstimatesStore.allCurrentViewPages
		.filter((pi) => {
			return pi.page.mergeWith === pageId;
		})
		.map((pi) => {
			const pageInfoData = EngineUtils.pageDataFromPageInfo(pi);
			return { ...pi, pageInfoData };
		});
	const showUnmerge = mergedWith.length > 0;
	return (
		<div className="self-stretch  pl-4 pr-6 pt-6 pb-8 bg-white border-b border-[#eaecf0] flex-col justify-start items-start gap-4 flex">
			<div className="self-stretch justify-between items-center inline-flex ">
				<div className=" flex-col justify-start items-start gap-0.5 inline-flex ">
					<div className="self-stretch h-[45px] flex-col justify-start items-start gap-1.5 flex ">
						<div className="self-stretch px-3.5 py-2.5 rounded-lg justify-start items-center gap-2 inline-flex ">
							<div className="grow shrink basis-0 h-[25px] justify-start items-center gap-2 flex">
								<div
									onBlur={(e) => {
										currentProjectStore.setMeasurementData(
											pageId,
											"overrideName",
											e.target.innerText.replaceAll("\n", " ").trim(),
											!isWizard,
										);
									}}
									contentEditable={true}
									suppressContentEditableWarning={true}
									className="text-[#0f1728] text-lg font-semibold font-['Hanken Grotesk'] leading-[25.20px] no-break min-w-[50px]"
								>
									{currentProjectStore.getMeasurementData(pageId, "overrideName") || roomName}
								</div>
							</div>
							{itemForDebugOnly && appFeatures.shouldShowEngineDebugInfo(getUserStore().user!) && (
								<div className="flex items-center">
									<input
										type="checkbox"
										checked={processedEstimatesStoreDebug.showQAPanel}
										onChange={(e) => {
											processedEstimatesStoreDebug.showQAPanel = e.target.checked;
										}}
									/>
									&nbsp;Show QA panel - ({appFeatures.shouldShowEngineDebugInfo(getUserStore().user!)})
								</div>
							)}
							{processedEstimatesStoreDebug.showQAPanel &&
								itemForDebugOnly &&
								appFeatures.shouldShowEngineDebugInfo(getUserStore().user!) && (
									<>
										<div
											className="text-red-500 btn no-break w-64"
											onClick={() => {
												try {
													RestAPI.fetchAPI("/rest/estimates/regenerateEstimate", "POST", {
														itemId: itemForDebugOnly.itemId,
														organizationId: getUserStore().user!.organizationId,
													});
												} catch (err) {}
											}}
										>
											Regenerate estimate
										</div>
										<div
											className="text-red-500 btn no-break w-64"
											onClick={() => {
												try {
													RestAPI.fetchAPI("/rest/estimates/regenerateEstimate", "POST", {
														itemId: itemForDebugOnly.itemId,
														organizationId: getUserStore().user!.organizationId,
														targetStatus: QueuedItemToProcessStatus.CellImproveWithOpenAICompleted,
													});
												} catch (err) {}
											}}
										>
											Regenerate last step
										</div>
										<div className="flex justify-between w-[80%]">
											<div className="flex flex-col">
												<div className="flex">
													<input
														type="checkbox"
														checked={processedEstimatesStoreDebug.showOnlyMappingError}
														onChange={(e) =>
															(processedEstimatesStoreDebug.showOnlyMappingError = e.target.checked)
														}
													/>
													<div className="ml-2">Show only mapping errors</div>
												</div>
												<div className="flex">
													<input
														type="checkbox"
														checked={processedEstimatesStoreDebug.dontShowMappingError}
														onChange={(e) =>
															(processedEstimatesStoreDebug.dontShowMappingError = e.target.checked)
														}
													/>
													<div className="ml-2">Dont show mapping errors</div>
												</div>
											</div>
											<div className="flex flex-col">
												<div className="flex">
													<input
														type="checkbox"
														checked={processedEstimatesStoreDebug.showOnlyApproved}
														onChange={(e) => (processedEstimatesStoreDebug.showOnlyApproved = e.target.checked)}
													/>
													<div className="ml-2">Show only approved</div>
												</div>
												<div className="flex">
													<input
														type="checkbox"
														checked={processedEstimatesStoreDebug.dontShowApproved}
														onChange={(e) => (processedEstimatesStoreDebug.dontShowApproved = e.target.checked)}
													/>
													<div className="ml-2">Don't show approved</div>
												</div>
											</div>
											<div className="flex flex-col">
												<div className="flex">
													<input
														type="checkbox"
														checked={processedEstimatesStoreDebug.showOnlyLooksLikeABug}
														onChange={(e) =>
															(processedEstimatesStoreDebug.showOnlyLooksLikeABug = e.target.checked)
														}
													/>
													<div className="ml-2">Show only looks like a bug</div>
												</div>
												<div className="flex">
													<input
														type="checkbox"
														checked={processedEstimatesStoreDebug.dontShowLooksLikeABug}
														onChange={(e) =>
															(processedEstimatesStoreDebug.dontShowLooksLikeABug = e.target.checked)
														}
													/>
													<div className="ml-2">Don't show looks like a bug</div>
												</div>
											</div>
											<button
												className="text-blue-500"
												onClick={() => {
													const sourceEstimate = window.prompt("Enter from estimate id");
													const destinationEstimate = currentProjectStore.currentEstimateID;
													if (sourceEstimate && destinationEstimate) {
														RestAPI.fetchAPI("/rest/estimates/copylineItemsForQA", "GET", {
															sourceEstimate,
															destinationEstimate,
														});
													}
												}}
											>
												Copy notes
											</button>
										</div>
									</>
								)}
						</div>
					</div>
				</div>
				<div className="justify-end items-center gap-4 flex">
					<div
					// className="p-2 bg-white rounded-lg shadow border border-[#cfd4dc] justify-center items-center flex btn"
					// onClick={() => setShowMergeWindow(true)}
					>
						{!isWizard && processedEstimatesStore.allCurrentViewPages.length > 1 && (
							<MergeButton onClick={() => setShowMergeWindow(true)} showUnmerge={showUnmerge} />
						)}
					</div>
					{/* <div className="w-9 h-9 p-2 rounded-lg justify-center items-center flex">
							<div className="w-5 h-5 relative flex-col justify-start items-start flex" />
						</div> */}
				</div>
			</div>
			<div>
				<div className="self-stretch justify-center items-start gap-4 inline-flex">
					<div className="grow shrink basis-0 flex-col justify-start items-start inline-flex"></div>
					{measurementCellNames.map((name, index) => {
						return (
							<OneMeasurementCell
								name={name[0]}
								measurement={name[1]}
								key={index}
								value={`${currentProjectStore.getMeasurementData(pageId, name[0])}`}
								onBlur={(newValue) => {
									currentProjectStore.setMeasurementData(pageId, name[0], parseFloat(newValue || "0"), !isWizard);
								}}
							/>
						);
					})}
				</div>
			</div>
			{showMergeWindow && (
				<TopMostPortal>
					<div className="w-[520px] h-[267px] bg-white rounded-xl shadow flex-col justify-between items-start inline-flex">
						<div className="flex w-full px-4">
							<div className="mt-6 ml-3 mb-2 w-full font-hanken-grotesk text-[20px] font-semibold leading-[28px] text-left  decoration-skip-ink-none no-break">
								{!showUnmerge ? <div>Merge rooms into {roomName}</div> : <div>Unmerge {roomName}</div>}
							</div>
							<div
								className="relative flex justify-end btn mt-4"
								onClick={() => {
									setShowMergeWindow(false);
								}}
							>
								<img src="/assets/ui/x-close.svg" className="w-5 h-5 mr-2 mt-2" />
							</div>
						</div>
						<div className="py-1 border-b border-gray-200 w-full"></div>
						{showUnmerge ? (
							<div className="p-4 ml-4 flex font-hanken-grotesk text-[20px]  leading-[28px] text-left  decoration-skip-ink-none relative top-2">
								{roomName} <img src="/assets/ui/split.svg" className="w-5 h-5 ml-2 mt-1.5" />{" "}
								<div className="relative left-2">
									<div className="-top-4 absolute no-break ">{roomName}</div>
									<div className="top-4 absolute no-break">
										{mergedWith
											.map((pi) => {
												return !pi.pageInfoData.roomName.trim() || pi.pageInfoData.roomName == "n/a"
													? "Unnamed Room"
													: pi.pageInfoData.roomName;
											})
											.join(",")}
									</div>
								</div>
							</div>
						) : (
							<div className="flex w-full px-4  gap-4 items-center justify-between mt-4">
								<div className="ml-3 font-hanken-grotesk text-[20px]  leading-[28px] text-left  decoration-skip-ink-none ">
									Merge:
								</div>
								<select
									className="p-2 mb-4 border rounded-lg w-full mt-4"
									value={mergePageId}
									onChange={(e) => setMergePageId(e.target.value)}
								>
									{processedEstimatesStore.allCurrentViewPages.map((pageInfo, index) => {
										const pageData = EngineUtils.pageDataFromPageInfo(pageInfo);
										return pageData.pageId === pageId ? null : (
											<option key={index} value={pageData.pageId}>
												{!pageData.roomName.trim() || pageData.roomName == "n/a"
													? "Unnamed Room"
													: pageData.roomName}
											</option>
										);
									})}
								</select>
								<div className="no-break w-full font-hanken-grotesk text-[20px]  leading-[28px] text-left  decoration-skip-ink-none">
									With {roomName}
								</div>
							</div>
						)}
						{/* <div className="flex w-full px-4  gap-4 items-center">
								{mergedWith.map((pageInfo, index) => {
									return (
										<div key={index} className="flex gap-2">
											<div className="text-lg font-semibold">Merged with:</div>
											<div className="text-blue-500">{pageInfo.pageInfoData.roomName}</div>
											<button
												className="text-red-500"
												onClick={async () => {
													const confirm = window.confirm(
														"Are you sure you want to break this merge? After breaking the page will be reloaded!"
													);
													if (!confirm) {
														return;
													}
													getEstimaticsCurrentProjectStore().showLoadingAnimation = true;
													try {
														await RestAPI.fetchAPI("/rest/estimates/mergePages", "POST", {
															estimateId: currentProjectStore.currentEstimateID,
															destinationPageId: "",
															sourcePageId: pageInfo.pageInfoData.pageId,
														});
														toastNotify("Merge broken", false);
														setTimeout(() => {
															window.location.reload();
														}, 1000);
													} catch (err) {
														toastNotify("Merge break failed", true);
													}
													getEstimaticsCurrentProjectStore().showLoadingAnimation = false;
												}}
											>
												Break merge
											</button>
										</div>
									);
								})}
							</div> */}
						<div className="flex justify-between gap-4 w-full p-4">
							<button className="px-4 py-2 bg-white rounded-lg border" onClick={() => setShowMergeWindow(false)}>
								Cancel
							</button>
							<button
								className="px-4 py-2 bg-[#194185] text-white rounded-lg"
								onClick={async () => {
									const confirm = window.confirm(
										showUnmerge
											? "Are you sure you want to break this merge? After breaking the page will be reloaded!"
											: "Are you sure you want to merge these rooms? After merge the page will be reloaded!",
									);
									if (!confirm) {
										return;
									}

									getEstimaticsCurrentProjectStore().showLoadingAnimation = true;
									try {
										await RestAPI.fetchAPI("/rest/estimates/mergePages", "POST", {
											estimateId: currentProjectStore.currentEstimateID,
											destinationPageId: showUnmerge ? "" : pageId,
											sourcePageId: mergePageId,
										});
										// logDebug("Merge", mergePageId, "into", pageId);
										setShowMergeWindow(false);
										toastNotify("Merge completed", false);
										setTimeout(() => {
											window.location.reload();
										}, 1000);
									} catch (err) {
										toastNotify("Merge failed", true);
									}
									getEstimaticsCurrentProjectStore().showLoadingAnimation = false;
								}}
							>
								{showUnmerge ? <span>Unmerge</span> : <span>Merge</span>}
							</button>
						</div>
					</div>
				</TopMostPortal>
			)}
		</div>
	);
});
